const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

export const unwrapApiData = (value) => {
  if (!value) return null;
  if (value.success !== undefined && value.data !== undefined) return unwrapApiData(value.data);
  if (isObject(value) && value.status === 'success' && value.data !== undefined) return value.data;
  return value;
};

const apiEnvelope = (value) => {
  if (value?.success !== undefined && value?.data !== undefined) return apiEnvelope(value.data);
  return isObject(value) && value.status === 'success' ? value : null;
};

export const extractEnergySiteIds = (value) => {
  const envelope = apiEnvelope(value);
  const ids = [
    ...(Array.isArray(envelope?.data) ? envelope.data.map((row) => row?.siteId) : []),
    ...(Array.isArray(envelope?.metadata?.siteSnapshots)
      ? envelope.metadata.siteSnapshots.map((snapshot) => snapshot?.siteId)
      : []),
  ].filter(Boolean).map(String);
  return [...new Set(ids)].sort();
};

export const getPath = (source, path) => {
  if (!source) return undefined;
  return path.split('.').reduce((current, key) => (
    current === undefined || current === null ? undefined : current[key]
  ), source);
};

const firstPresent = (sources, paths) => {
  for (const source of sources) {
    for (const path of paths) {
      const value = getPath(source, path);
      if (value !== undefined && value !== null && value !== '') return value;
    }
  }
  return null;
};

const numberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const textOrNull = (value) => (
  value === null || value === undefined || value === '' ? null : String(value)
);

const asArray = (value) => {
  const unwrapped = unwrapApiData(value);
  if (Array.isArray(unwrapped)) return unwrapped;
  if (!isObject(unwrapped)) return [];
  for (const key of ['items', 'observations', 'snapshots', 'records', 'results', 'rows']) {
    if (Array.isArray(unwrapped[key])) return unwrapped[key];
  }
  return [];
};

const recordBody = (record) => (
  record?.payload || record?.normalized || record?.telemetry || record?.data || record || {}
);

const recordType = (record) => String(
  record?.messageType || record?.type || record?.schemaName || record?.topic || ''
).toLowerCase();

const recordsMatching = (records, terms) => records
  .filter((record) => terms.some((term) => recordType(record).includes(term)))
  .map(recordBody);

const combineSources = (...groups) => groups.flat().filter(Boolean);

const readNumber = (sources, paths) => numberOrNull(firstPresent(sources, paths));
const readText = (sources, paths) => textOrNull(firstPresent(sources, paths));

const metricPaths = {
  solarKw: ['power.solarKw', 'site.power.generationKw', 'site.power.solarKw', 'site.solar.powerKw', 'solar.powerKw', 'generation.powerKw', 'solarKw'],
  loadKw: ['power.loadKw', 'site.power.servedLoadKw', 'site.power.loadKw', 'site.load.powerKw', 'load.servedPowerKw', 'load.powerKw', 'customerLoad.powerKw', 'loadKw'],
  bessKw: ['power.bessKw', 'site.power.batteryKw', 'site.power.bessKw', 'site.bess.powerKw', 'battery.powerKw', 'bess.netPowerKw', 'bess.actualPowerKw', 'bess.pcs.actualPowerKw', 'bess.powerKw', 'bessKw'],
  gridKw: ['power.gridKw', 'site.power.gridKw', 'site.grid.powerKw', 'grid.powerKw', 'pcc.powerKw', 'gridKw'],
};

const normalizeCanopies = (records, equipment, canopyInventory) => {
  const fleetInventory = asArray(canopyInventory);
  const fleetCanopyIds = new Set(fleetInventory.map((item) => (
    firstPresent([item, recordBody(item)], ['canopyId', 'assetId', 'deviceId', 'id'])
  )).filter(Boolean).map(String));
  const selectedFleetEntry = (entry) => {
    if (!fleetCanopyIds.size) return true;
    const id = firstPresent([entry, recordBody(entry)], ['canopyId', 'assetId', 'deviceId', 'id']);
    return id !== null && id !== undefined && fleetCanopyIds.has(String(id));
  };
  const telemetry = records.filter((record) => (
    recordType(record).includes('canopy') && selectedFleetEntry(record)
  ));
  const equipmentInventory = asArray(equipment).filter((item) => {
    const type = String(item.assetType || item.type || item.role || '').toLowerCase();
    return (type.includes('canopy') || item.canopyId) && selectedFleetEntry(item);
  });
  const byId = new Map();

  const mergeEntry = (entry, index, namePriority) => {
    const body = recordBody(entry);
    const id = firstPresent([entry, body], ['canopyId', 'assetId', 'deviceId', 'id']) || `canopy-${index + 1}`;
    const existing = byId.get(String(id)) || {};
    const powerKw = readNumber([body, entry], ['inverter.acOutput.activePowerKw', 'acOutput.activePowerKw', 'production.powerKw', 'powerKw']);
    const powerW = readNumber([body, entry], ['sabalController.energy.inverter.acOutput.powerW']);
    const providedName = readText([body, entry], ['canopyName', 'name', 'assetName']);
    const shouldReplaceName = providedName && namePriority >= (existing.namePriority || 0);
    byId.set(String(id), {
      ...existing,
      id: String(id),
      name: shouldReplaceName
        ? providedName
        : (existing.name || `Canopy ${String(id).replace(/\D/g, '') || index + 1}`),
      namePriority: shouldReplaceName ? namePriority : (existing.namePriority || 0),
      powerKw: powerKw ?? (powerW === null ? (existing.powerKw ?? null) : powerW / 1000),
      state: readText([body, entry], ['sabalController.operating.mode', 'sabalController.systemInfo.canopyStateText', 'mechanical.state', 'canopy.state', 'operatingState', 'state']) ?? existing.state ?? null,
      angleDeg: readNumber([body, entry], ['sabalController.operating.actualTiltDeg', 'mechanical.angleDeg', 'canopy.angleDeg', 'angleDeg']) ?? existing.angleDeg ?? null,
      quality: readText([body, entry], ['sabalController.energy.inverter.dataStatus', 'quality.overall', 'quality', 'acquisition.status']) ?? existing.quality ?? null,
      sourceTime: readText([body, entry], ['timestamp', 'sourceTime', 'observedAt']) ?? existing.sourceTime ?? null,
    });
  };

  // Fleet inventory owns identity and display name. Energy equipment and
  // telemetry enrich those same cards with operating values when available.
  equipmentInventory.forEach((entry, index) => mergeEntry(entry, index, 1));
  telemetry.forEach((entry, index) => mergeEntry(entry, index, 2));
  fleetInventory.forEach((entry, index) => mergeEntry(entry, index, 3));

  const list = Array.from(byId.values()).map(({ namePriority, ...canopy }) => canopy);
  if (list.length >= 10) return list.slice(0, 10);

  // The demonstration contract fixes ten numbered canopy positions. Empty
  // positions are rendered explicitly as unavailable, never as zero output.
  const usedNumbers = new Set(list.map((item) => Number(String(item.id).replace(/\D/g, ''))).filter(Boolean));
  for (let number = 1; number <= 10; number += 1) {
    if (!usedNumbers.has(number)) {
      list.push({
        id: `canopy-${number}`,
        name: `Canopy ${String(number).padStart(2, '0')}`,
        powerKw: null,
        state: 'unavailable',
        angleDeg: null,
        quality: 'unavailable',
        sourceTime: null,
      });
    }
  }
  return list.slice(0, 10);
};

const normalizeEquipment = (equipment) => asArray(equipment).map((item) => ({
  ...item,
  assetType: item.assetType || item.equipmentRole || item.role || null,
  manufacturer: item.manufacturer || item.identityData?.manufacturer || item.identity?.manufacturer || null,
  model: item.model || item.identityData?.model || item.identity?.model || null,
  digitalId: item.digitalId || item.identityData?.digitalId || item.identity?.digitalId || null,
  serialNumber: item.serialNumber || item.identityData?.serialNumber || item.identity?.serialNumber || null,
  hardwareVersion: item.hardwareVersion || item.versionData?.hardware || item.versions?.hardware || null,
  firmwareVersion: item.firmwareVersion || item.versionData?.firmware || item.versions?.firmware || null,
  softwareVersion: item.softwareVersion || item.versionData?.software || item.versions?.software || null,
  ratings: item.ratings || item.ratingData || {},
}));

const normalizeHistorySeries = (history) => {
  if (apiEnvelope(history)?.metadata?.bucketSeconds) return asArray(history);
  const byTime = new Map();
  asArray(history).forEach((record) => {
    const body = recordBody(record);
    const time = readText([body, record], ['timestamp', 'observedAt']);
    if (!time) return;
    const point = byTime.get(time) || {
      time, solarKw: null, loadKw: null, bessKw: null, gridKw: null, socPct: null,
    };
    const type = recordType(record);
    if (type.includes('site.telemetry')) {
      point.solarKw = readNumber([body], metricPaths.solarKw);
      point.loadKw = readNumber([body], metricPaths.loadKw);
      point.bessKw = readNumber([body], metricPaths.bessKw);
      point.gridKw = readNumber([body], metricPaths.gridKw);
    } else if (type.includes('bess')) {
      point.bessKw = readNumber([body], metricPaths.bessKw);
      point.socPct = readNumber([body], ['bess.socPct']);
    } else if (type.includes('load.telemetry')) {
      point.loadKw = readNumber([body], metricPaths.loadKw);
    } else if (type.includes('pcc.telemetry')) {
      point.gridKw = readNumber([body], metricPaths.gridKw);
    }
    byTime.set(time, point);
  });
  return [...byTime.values()].sort((left, right) => Date.parse(left.time) - Date.parse(right.time));
};

const normalizeRollups = (rollups) => asArray(rollups).map((entry) => ({
  id: entry.energyRollupId || null,
  time: entry.periodStart || null,
  periodEnd: entry.periodEnd || null,
  metric: entry.metric || null,
  value: numberOrNull(entry.value),
  unit: entry.unit || null,
  status: entry.status || 'unavailable',
  sourceKind: entry.sourceKind || null,
  coveragePct: Number(entry.expectedSeconds) > 0
    ? (100 * Number(entry.coveredSeconds || 0)) / Number(entry.expectedSeconds)
    : null,
  revision: entry.revision || null,
}));

const preferredRollupTotal = (rows, counterMetric, estimateMetric) => {
  const complete = (metric) => rows.filter((row) => (
    row.metric === metric && row.status === 'complete' && row.value !== null
  ));
  const selected = complete(counterMetric);
  const values = selected.length ? selected : complete(estimateMetric);
  return values.length ? values.reduce((sum, row) => sum + row.value, 0) : null;
};

const FAULT_PATHS = [
  'site.fault', 'ems.fault', 'bess.fault', 'load.fault', 'pcc.fault',
  'sabalController.operating.fault', 'sabalController.energy.inverter.fault',
];

const normalizeFaultEvents = (records) => {
  const byIdentity = new Map();
  records.forEach((record) => {
    const body = recordBody(record);
    FAULT_PATHS.forEach((path) => {
      const faults = getPath(body, path);
      if (!Array.isArray(faults)) return;
      faults.forEach((fault) => {
        if (!fault?.code && !fault?.description) return;
        const time = body.timestamp || record.observedAt || null;
        const identity = [record.siteId, record.deviceId, fault.code, fault.description].filter(Boolean).join('|');
        const previous = byIdentity.get(identity);
        if (!previous || Date.parse(time) > Date.parse(previous.time)) {
          byIdentity.set(identity, {
            id: identity,
            severity: 'fault',
            code: fault.code || null,
            message: fault.description || fault.code,
            time,
            siteId: record.siteId || body.siteId || null,
            deviceId: record.deviceId || body.deviceId || null,
            source: path,
          });
        }
      });
    });
  });
  return [...byIdentity.values()].sort((left, right) => Date.parse(right.time) - Date.parse(left.time));
};

/**
 * Build the stable UI model from the accepted DBAM success envelopes. Legacy
 * aliases remain confined here for existing telemetry responses; the energy
 * metadata, rollup, reconciliation, production, and costing paths below bind
 * directly to the completed EP-04/EP-05 contracts.
 */
export const buildEnergyViewModel = ({
  latest,
  history,
  equipment,
  production,
  reconciliation,
  rollups,
  demandRollups,
  costs,
  costSummary,
  canopyInventory,
  leaseEconomics,
  todayProduction,
  todayRollups,
  windowRange,
} = {}) => {
  const latestEnvelope = apiEnvelope(latest);
  const latestData = unwrapApiData(latest);
  const latestRecords = asArray(latestData);
  const latestRoot = isObject(latestData) ? latestData : {};
  const latestMetadata = latestEnvelope?.metadata || {};
  const reconciliationEnvelope = apiEnvelope(reconciliation);
  const reconciliationData = unwrapApiData(reconciliation) || {};
  const reconciliationMetadata = reconciliationEnvelope?.metadata || {};
  const reconciliationRows = asArray(reconciliationData);
  const latestReconciliation = [...reconciliationRows].sort((left, right) => (
    Date.parse(right.periodStart) - Date.parse(left.periodStart)
  ))[0] || {};
  const productionEnvelope = apiEnvelope(production);
  const productionMetadata = productionEnvelope?.metadata || {};
  const rollupEnvelope = apiEnvelope(rollups);
  const rollupMetadata = rollupEnvelope?.metadata || {};
  const demandRollupEnvelope = apiEnvelope(demandRollups);
  const demandRollupMetadata = demandRollupEnvelope?.metadata || {};
  const rollupRows = [...normalizeRollups(rollups), ...normalizeRollups(demandRollups)];
  const servedDemandValues = rollupRows.filter((row) => (
    row.metric === 'served_load_demand_kw' && row.status === 'complete' && row.value !== null
  )).map((row) => row.value);
  const costData = unwrapApiData(costs) || {};
  const costSummaryData = unwrapApiData(costSummary) || {};

  const siteBodies = recordsMatching(latestRecords, ['site.telemetry', 'site_telemetry', 'site/']);
  const bessBodies = recordsMatching(latestRecords, ['battery', 'bess']);
  const emsBodies = recordsMatching(latestRecords, ['ems.telemetry', 'ems_telemetry']);
  const loadBodies = recordsMatching(latestRecords, ['load.telemetry', 'load_telemetry']);
  const pccBodies = recordsMatching(latestRecords, ['pcc.telemetry', 'pcc_telemetry']);
  const currentCalculations = latestMetadata.calculations || {};
  const currentBalance = currentCalculations.activePowerBalance || {};
  const sources = combineSources(latestRoot, latestRecords, siteBodies, latestRecords.map(recordBody));
  const normalizedEquipment = normalizeEquipment(equipment);
  const bessEquipment = normalizedEquipment.filter((item) => {
    const role = String(item.assetType || '').toLowerCase();
    const manufacturer = String(item.manufacturer || '').trim().toLowerCase();
    const model = String(item.model || '').trim().toLowerCase();
    if (
      manufacturer === 'sabal simulator'
      || model === 'external-bidirectional-pcs'
      || model === 'two-rack-external-pcs'
    ) {
      return false;
    }
    return role.includes('bess') || role.includes('battery') || role === 'bms' || role === 'pcs';
  });
  const bessSources = combineSources(bessBodies, emsBodies, latestRoot.bess, latestRoot.battery, sources);
  const emsSources = combineSources(emsBodies, latestRoot.ems, ...siteBodies.map((item) => item.ems), sources);
  const loadSources = combineSources(loadBodies, latestRoot.load, sources);
  const pccSources = combineSources(pccBodies, latestRoot.pcc, latestRoot.grid, ...siteBodies.map((item) => item.pcc || item.grid), sources);
  const canopyBodies = recordsMatching(latestRecords, ['canopy.telemetry']);

  const power = {
    solarKw: readNumber(sources, metricPaths.solarKw),
    loadKw: readNumber(sources, metricPaths.loadKw),
    bessKw: readNumber(sources, metricPaths.bessKw),
    gridKw: readNumber(sources, metricPaths.gridKw),
    residualKw: numberOrNull(currentBalance.residualKw),
    toleranceKw: numberOrNull(currentBalance.toleranceKw),
  };
  const balancedValue = currentBalance.balanceWithinTolerance;
  power.balanced = typeof balancedValue === 'boolean'
    ? balancedValue
    : (power.residualKw !== null && power.toleranceKw !== null
      ? Math.abs(power.residualKw) <= power.toleranceKw
      : null);

  const chartMetadata = apiEnvelope(history)?.metadata || {};
  const selectedSummary = chartMetadata.periodSummary || {};
  const productionCounter = numberOrNull(productionMetadata.totals?.inverterRegisterCounterDerivedKwh?.value);
  const productionEstimate = numberOrNull(productionMetadata.totals?.sabalSampledPowerEstimateKwh?.value);
  const energy = {
    generatedKwh: productionCounter ?? productionEstimate ?? numberOrNull(selectedSummary.solarKw?.positiveKwh)
      ?? (!chartMetadata.bucketSeconds ? preferredRollupTotal(rollupRows, 'solar_production_counter_kwh', 'generation_kwh') : null),
    consumedKwh: numberOrNull(selectedSummary.loadKw?.positiveKwh),
    importedKwh: numberOrNull(selectedSummary.gridKw?.positiveKwh),
    exportedKwh: numberOrNull(selectedSummary.gridKw?.negativeKwh),
    chargedKwh: numberOrNull(selectedSummary.bessKw?.negativeKwh),
    dischargedKwh: numberOrNull(selectedSummary.bessKw?.positiveKwh),
    inverterRegisterKwh: productionCounter,
    sampledEstimateKwh: productionEstimate,
  };
  // Dashboard Today never uses the detail view's selected history window.
  const todayProductionMetadata = apiEnvelope(todayProduction)?.metadata || {};
  const todayRows = normalizeRollups(todayRollups);
  const todayEnergy = {
    generatedKwh: numberOrNull(todayProductionMetadata.totals?.inverterRegisterCounterDerivedKwh?.value)
      ?? numberOrNull(todayProductionMetadata.totals?.sabalSampledPowerEstimateKwh?.value),
    inverterRegisterKwh: numberOrNull(todayProductionMetadata.totals?.inverterRegisterCounterDerivedKwh?.value),
    sampledEstimateKwh: numberOrNull(todayProductionMetadata.totals?.sabalSampledPowerEstimateKwh?.value),
    consumedKwh: readNumber(loadSources, ['load.energy.servedTodayKwh'])
      ?? preferredRollupTotal(todayRows, 'load_counter_kwh', 'load_kwh'),
    importedKwh: readNumber(pccSources, ['pcc.energy.importedTodayKwh'])
      ?? preferredRollupTotal(todayRows, 'grid_import_counter_kwh', 'grid_import_kwh'),
    exportedKwh: readNumber(pccSources, ['pcc.energy.exportedTodayKwh'])
      ?? preferredRollupTotal(todayRows, 'grid_export_counter_kwh', 'grid_export_kwh'),
    chargedKwh: readNumber(bessSources, ['bess.energy.chargedTodayKwh'])
      ?? preferredRollupTotal(todayRows, 'bess_charge_counter_kwh', 'bess_charge_kwh'),
    dischargedKwh: readNumber(bessSources, ['bess.energy.dischargedTodayKwh'])
      ?? preferredRollupTotal(todayRows, 'bess_discharge_counter_kwh', 'bess_discharge_kwh'),
  };

  const sourceTime = latestMetadata.asOf || readText(sources, ['timestamp', 'sourceTime', 'observedAt', 'latestSourceTime']);
  const receivedAt = latestRecords.reduce((latest, record) => {
    const candidate = readText([record], ['receivedAt', 'cloudReceivedAt', 'updatedAt']);
    if (!candidate || !Number.isFinite(Date.parse(candidate))) return latest;
    return !latest || Date.parse(candidate) > Date.parse(latest) ? candidate : latest;
  }, null) || readText(sources, ['receivedAt', 'cloudReceivedAt', 'updatedAt']);
  const quality = readText(sources, ['quality.overall', 'quality', 'site.dataStatus', 'dataStatus', 'acquisition.status']) || 'unavailable';

  return {
    meta: {
      sourceTime,
      receivedAt,
      sourceType: latestMetadata.sourceType || latestMetadata.sourceKind ||
        readText(sources, ['sourceType', 'sourceKind', 'site.sourceType']) ||
        (latestRecords.some((record) => String(record.payload?.bess?.contractStatus || record.payload?.contractStatus || '').includes('simulator')) ? 'Simulated' : 'Observed'),
      quality,
      acquisitionStatus: readText(sources, ['site.dataStatus', 'acquisition.status', 'acquisitionStatus', 'dataStatus']),
      schemaVersion: readText(sources, ['schemaVersion']),
      coveragePct: readNumber([costSummaryData, productionMetadata, rollupMetadata, reconciliationMetadata], ['coverage.coveragePct', 'coverage.telemetry.coveragePct']),
      siteId: latestMetadata.siteSnapshots?.[0]?.siteId || latestRecords[0]?.siteId || null,
      calculationVersion: latestMetadata.calculationVersion || null,
    },
    power,
    energy,
    todayEnergy,
    leaseEconomics: unwrapApiData(leaseEconomics) || {},
    history: { ...chartMetadata, range: chartMetadata.range || windowRange },
    bess: {
      state: readText(bessSources, ['bess.state', 'state', 'operatingState', 'battery.state']),
      socPct: readNumber(bessSources, ['bess.socPct', 'socPct', 'stateOfChargePct', 'battery.socPct']),
      storedKwh: readNumber(bessSources, ['bess.usableEnergyAboveReserveKwh', 'bess.storedEnergyKwh', 'storedKwh', 'usableEnergyKwh', 'battery.storedKwh']),
      usableCapacityKwh: readNumber(combineSources(bessSources, bessEquipment), ['bess.usableEnergyAboveReserveKwh', 'usableEnergyAboveReserveKwh', 'usableCapacityKwh', 'capacity.usableKwh', 'ratings.capacityKwh']),
      minSocPct: readNumber(bessSources, ['bess.limits.minimumSocPct', 'ems.policy.minimumSocPct', 'limits.minimumSocPct', 'limits.minSocPct', 'minSocPct', 'reserveSocPct']),
      maxSocPct: readNumber(bessSources, ['bess.limits.maximumSocPct', 'ems.policy.maximumSocPct', 'limits.maximumSocPct', 'limits.maxSocPct', 'maxSocPct']),
      requestedKw: readNumber(bessSources, ['ems.dispatch.requestedKw', 'dispatch.requestedKw', 'requestedPowerKw', 'requestedKw']),
      constrainedKw: readNumber(bessSources, ['ems.dispatch.constrainedKw', 'dispatch.constrainedKw', 'constrainedPowerKw', 'constrainedKw']),
      pcsKw: readNumber(bessSources, ['bess.pcs.actualPowerKw', 'bess.pcs.powerKw', 'pcs.activePowerKw', 'bess.actualPowerKw']),
      auxiliaryKw: readNumber(bessSources, ['bess.bop.auxiliaryPowerKw', 'bess.auxiliaryPowerKw']),
      netKw: readNumber(bessSources, ['bess.netPowerKw', 'bess.actualPowerKw', ...metricPaths.bessKw]),
      actualKw: readNumber(bessSources, ['bess.netPowerKw', 'bess.actualPowerKw', 'bess.pcs.actualPowerKw', 'bess.pcs.powerKw', 'pcs.activePowerKw', 'actualPowerKw', 'powerKw', ...metricPaths.bessKw]),
      availableChargeKw: readNumber(bessSources, ['bess.limits.availableChargeKw', 'limits.availableChargeKw', 'availableChargeKw']),
      availableDischargeKw: readNumber(bessSources, ['bess.limits.availableDischargeKw', 'limits.availableDischargeKw', 'availableDischargeKw']),
      dcVoltageV: readNumber(bessSources, ['bess.bms.voltageV', 'dc.voltageV', 'battery.voltageV', 'dcVoltageV']),
      dcCurrentA: readNumber(bessSources, ['bess.bms.currentA', 'dc.currentA', 'battery.currentA', 'dcCurrentA']),
      acVoltageV: readNumber(bessSources, ['bess.pcs.voltageV', 'pcs.acVoltageV', 'ac.voltageV', 'acVoltageV']),
      acCurrentA: readNumber(bessSources, ['bess.pcs.currentA', 'pcs.acCurrentA', 'ac.currentA', 'acCurrentA']),
      frequencyHz: readNumber(bessSources, ['bess.pcs.frequencyHz', 'pcs.frequencyHz', 'ac.frequencyHz', 'frequencyHz']),
      cellTempC: readNumber(bessSources, ['bess.temperatures.batteryC', 'temperatures.cellC', 'cellTemperatureC', 'cellTempC']),
      ambientTempC: readNumber(bessSources, ['bess.temperatures.ambientC', 'temperatures.ambientC', 'ambientTemperatureC', 'ambientTempC']),
      pcsTempC: readNumber(bessSources, ['bess.temperatures.pcsC', 'temperatures.pcsC', 'pcsTemperatureC', 'pcsTempC']),
      sohPct: readNumber(bessSources, ['bess.sohPct', 'stateOfHealthPct', 'sohPct']),
      limitingConstraint: readText(bessSources, ['bess.limits.bindingConstraint', 'bess.limits.reasonCode', 'dispatch.limitingConstraint', 'limitingConstraint', 'activeConstraint']),
      equipment: bessEquipment,
    },
    ems: {
      mode: readText(emsSources, ['ems.mode', 'mode', 'operatingMode']),
      objective: readText(emsSources, ['ems.objective', 'objective', 'activeObjective']),
      requestedKw: readNumber(emsSources, ['ems.dispatch.requestedKw', 'dispatch.requestedKw', 'requestedPowerKw', 'requestedKw']),
      constrainedKw: readNumber(emsSources, ['ems.dispatch.constrainedKw', 'dispatch.constrainedKw', 'constrainedPowerKw', 'constrainedKw']),
      actualKw: readNumber(emsSources, ['ems.dispatch.actualKw', 'dispatch.actualKw', 'actualPowerKw', 'actualKw']),
      constraint: readText(emsSources, ['ems.dispatch.bindingConstraint', 'dispatch.bindingConstraint', 'bindingConstraint', 'constraint']),
      reason: readText(emsSources, ['ems.dispatch.reason', 'ems.dispatch.reasonCode', 'dispatch.reason', 'dispatch.reasonCode', 'decisionReason', 'reason']),
      acknowledgement: readText(emsSources, ['ems.dispatch.ackStatus', 'ems.dispatch.acknowledgement', 'dispatch.acknowledgement', 'acknowledgement', 'ack']),
      authority: readText(emsSources, ['ems.authority', 'authority', 'controlAuthority']),
      demandTargetKw: readNumber(emsSources, ['ems.policy.demandTargetKw', 'ems.policy.targetDemandKw', 'policy.demandTargetKw', 'policy.targetDemandKw', 'demandTargetKw', 'targetDemandKw']),
      exportLimitKw: readNumber(emsSources, ['ems.policy.exportLimitKw', 'policy.exportLimitKw', 'exportLimitKw']),
    },
    load: {
      powerKw: readNumber(loadSources, ['load.servedPowerKw', 'servedPowerKw']) ?? power.loadKw,
      consumedKwh: energy.consumedKwh,
      intervalPeakKw: servedDemandValues.length ? Math.max(...servedDemandValues)
        : (numberOrNull(demandRollupMetadata.peaks?.servedLoadDemandKw?.peakKw)
          ?? readNumber(sources, ['load.intervalPeakKw', 'demand.intervalPeakKw', 'intervalPeakKw'])),
      billingPeakKw: readNumber(sources, ['load.billingPeakKw', 'demand.billingPeakKw', 'billingPeakKw']),
      profile: readText(loadSources, ['load.profile', 'load.profileName', 'profileName']),
    },
    pcc: {
      powerKw: power.gridKw,
      importedKwh: energy.importedKwh,
      exportedKwh: energy.exportedKwh,
      voltageV: readNumber(pccSources, ['pcc.siteSide.voltageV', 'pcc.utilitySide.voltageV', 'pcc.voltageV', 'voltageV', 'ac.voltageV', 'lineVoltageV']),
      currentA: readNumber(pccSources, ['pcc.currentA', 'currentA', 'ac.currentA', 'lineCurrentA']),
      frequencyHz: readNumber(pccSources, ['pcc.siteSide.frequencyHz', 'pcc.utilitySide.frequencyHz', 'pcc.frequencyHz', 'frequencyHz', 'ac.frequencyHz']),
      powerFactor: readNumber(pccSources, ['pcc.powerFactor', 'powerFactor', 'ac.powerFactor']),
      communicationState: readText(pccSources, ['pcc.connectionState', 'communication.state', 'communicationState']),
      meterQuality: readText(pccSources, ['pcc.dataStatus', 'meter.quality', 'quality']),
    },
    costs: {
      currency: readText(combineSources(costData, costSummaryData), ['currency', 'currencyCode', 'metadata.currency']),
      importEnergyPricePerKwh: readText([costData], ['importEnergyPricePerKwh.value']),
      exportCreditPerKwh: readText([costData], ['exportCreditPerKwh.value']),
      utilityRatePerHour: readText([costData], ['utilityEnergyCostRate.value', 'currentUtilityEnergyCostRate.value', 'utilityRatePerHour', 'measures.utilityRatePerHour.value']),
      modeledGenerationRatePerHour: readText([costData], ['modeledGenerationCostRate.value', 'modeledGenerationRatePerHour', 'measures.modeledGenerationRatePerHour.value']),
      allocatedLeaseRatePerHour: readText([costData], ['allocatedLeaseCostRate.value']),
      periodUtilityCost: readText([costSummaryData], ['periodUtilityCost.value', 'periodUtilityCost', 'measures.periodUtilityCost.value']),
      periodLeaseCost: readText([costSummaryData], ['periodLeaseCost.value']),
      periodCustomerCost: readText([costSummaryData], ['periodCustomerCost.value']),
      averageUtilityCostPerKwh: readText([costSummaryData], ['averageUtilityEnergyCost.value']),
      averageLeaseCostPerKwh: readText([costSummaryData], ['averageLeaseEnergyCost.value']),
      leaseEnergyDenominator: readText([costSummaryData], ['averageLeaseEnergyCost.denominator.name']),
      averageCustomerCostPerKwh: readText([costSummaryData], ['averageCustomerEnergyCost.value', 'averageCustomerCostPerKwh', 'measures.averageCustomerCostPerKwh.value']),
      modeledLcoePerKwh: readText(combineSources(costSummaryData, costData), ['modeledLcoe.value', 'modeledLcoePerKwh', 'measures.modeledLcoePerKwh.value']),
      outageEconomicValue: readText([costSummaryData], ['outageEconomicValue.value', 'outageEconomicValue']),
      calculationVersion: readText(combineSources(costSummaryData, costData), ['calculationVersion', 'metadata.calculationVersion']),
      unavailableReason: firstPresent(combineSources(costSummaryData, costData), ['unavailableReason', 'reasonCode', 'reasonCodes.0']),
    },
    canopies: normalizeCanopies(latestRecords, equipment, canopyInventory),
    series: normalizeHistorySeries(history),
    rollups: rollupRows,
    reconciliation: {
      rows: reconciliationRows,
      residualKwh: numberOrNull(latestReconciliation.residualKwh),
      toleranceKwh: numberOrNull(latestReconciliation.toleranceKwh),
      residualPct: numberOrNull(latestReconciliation.residualPct),
      balanceWithinTolerance: typeof latestReconciliation.balanceWithinTolerance === 'boolean'
        ? latestReconciliation.balanceWithinTolerance
        : null,
      status: latestReconciliation.status || 'unavailable',
    },
    events: normalizeFaultEvents([...latestRecords, ...asArray(history)]),
    equipment: normalizedEquipment,
  };
};

export const formatValue = (value, unit, options = {}) => {
  if (value === null || value === undefined || value === '' || /^unavailable$/i.test(String(value))) return '-';
  const numeric = Number(value);
  const display = Number.isFinite(numeric)
    ? new Intl.NumberFormat('en-US', {
      minimumFractionDigits: options.minimumFractionDigits ?? 0,
      maximumFractionDigits: options.maximumFractionDigits ?? 3,
    }).format(numeric)
    : String(value);
  return unit ? `${display} ${unit}` : display;
};

export const signedPowerLabel = (value, positiveLabel, negativeLabel) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '-';
  if (Number(value) > 0) return positiveLabel;
  if (Number(value) < 0) return negativeLabel;
  return 'Idle';
};
